// src/pages/perfil/Perfil.js
import React, { useEffect, useState } from 'react';
import './perfil.css';
import Footer from '../../components/footer/Footer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faWeightScale,
  faBell,
  faPen,
  faCheck,
  faXmark,
  faDroplet,
  faUtensils,
  faBolt,
} from '@fortawesome/free-solid-svg-icons';

const API_BASE = process.env.REACT_APP_API_BASE;

const ABAS = [
  { id: 'basicos', label: 'Dados Básicos', icone: faUser },
  { id: 'metas', label: 'Medidas & Metas', icone: faWeightScale },
  { id: 'notificacoes', label: 'Notificações', icone: faBell },
];

// ==========================================================
// Campo editável genérico — mostra valor + lápis; ao clicar,
// vira input com Salvar/Cancelar
// ==========================================================
const CampoEditavel = ({
  label,
  valorExibido,
  valorBruto,
  tipo = 'text',
  opcoes = null,
  onSalvar,
  salvando,
  ajuda = null,
}) => {
  const [editando, setEditando] = useState(false);
  const [valorTemp, setValorTemp] = useState(valorBruto ?? '');

  useEffect(() => {
    setValorTemp(valorBruto ?? '');
  }, [valorBruto]);

  const iniciarEdicao = () => {
    setValorTemp(valorBruto ?? '');
    setEditando(true);
  };

  const cancelar = () => {
    setValorTemp(valorBruto ?? '');
    setEditando(false);
  };

  const confirmar = async () => {
    await onSalvar(valorTemp);
    setEditando(false);
  };

  return (
    <div className="campo-editavel">
      <label>{label}</label>

      {!editando ? (
        <div className="campo-editavel-visualizacao">
          <span className="campo-editavel-valor">{valorExibido}</span>
          <button
            type="button"
            className="campo-editavel-lapis"
            onClick={iniciarEdicao}
            title="Editar"
          >
            <FontAwesomeIcon icon={faPen} />
          </button>
        </div>
      ) : (
        <div className="campo-editavel-edicao">
          {opcoes ? (
            <select
              value={valorTemp}
              onChange={(e) => setValorTemp(e.target.value)}
              autoFocus
            >
              <option value="">Selecione...</option>
              {opcoes.map((op) => (
                <option key={op.value} value={op.value}>
                  {op.label}
                </option>
              ))}
            </select>
          ) : (
            <input
              type={tipo}
              step={tipo === 'number' ? '0.1' : undefined}
              value={valorTemp}
              onChange={(e) => setValorTemp(e.target.value)}
              autoFocus
            />
          )}

          <button
            type="button"
            className="campo-editavel-btn-confirmar"
            onClick={confirmar}
            disabled={salvando}
          >
            <FontAwesomeIcon icon={faCheck} />
          </button>
          <button
            type="button"
            className="campo-editavel-btn-cancelar"
            onClick={cancelar}
            disabled={salvando}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      )}

      {ajuda && <small>{ajuda}</small>}
    </div>
  );
};

const Perfil = () => {
  const [perfil, setPerfil] = useState({
    nome: '',
    email: '',
    genero: '',
    data_nascimento: '',
    peso_atual: '',
    altura_cm: '',
    objetivo: '',
    nivel_atividade: '',
    calorias_meta: '',
    calorias_meta_manual: false,
    meta_agua: '',
  });

  const [abaAtiva, setAbaAtiva] = useState('basicos');
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');

  const [avisoNotificacoes, setAvisoNotificacoes] = useState(false);

  const carregarPerfil = async () => {
    setCarregando(true);
    setErro('');

    try {
      const res = await fetch(`${API_BASE}/get-perfil.php`, {
        credentials: 'include',
      });

      const data = await res.json();

      if (data.status !== 'sucesso' || !data.perfil) {
        setErro(data.mensagem || 'Não foi possível carregar seu perfil.');
        setCarregando(false);
        return;
      }

      const p = data.perfil;

      setPerfil({
        nome: p.nome || '',
        email: p.email || '',
        genero: p.genero || '',
        data_nascimento: p.data_nascimento || '',
        peso_atual: p.peso_atual ?? '',
        altura_cm: p.altura_cm ?? '',
        objetivo: p.objetivo || '',
        nivel_atividade: p.nivel_atividade || '',
        calorias_meta: p.calorias_meta ?? '',
        calorias_meta_manual: !!p.calorias_meta_manual,
        meta_agua: p.meta_agua ?? '',
      });
    } catch (e) {
      console.error(e);
      setErro('Erro ao carregar perfil. Tente novamente em alguns instantes.');
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregarPerfil();
  }, []);

  // ==========================================================
  // Salva UM campo por vez, reaproveitando o merge do backend
  // ==========================================================
  const salvarCampo = async (payloadParcial) => {
    setSalvando(true);
    setErro('');
    setSucesso('');

    try {
      const res = await fetch(`${API_BASE}/atualizar-perfil.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payloadParcial),
      });

      const data = await res.json();

      if (data.status !== 'sucesso') {
        setErro(data.mensagem || 'Erro ao atualizar perfil.');
        return;
      }

      setSucesso('Atualizado com sucesso.');
      setTimeout(() => setSucesso(''), 2500);

      if (data.perfil) {
        setPerfil((prev) => ({
          ...prev,
          objetivo: data.perfil.objetivo ?? prev.objetivo,
          nivel_atividade: data.perfil.nivel_atividade ?? prev.nivel_atividade,
          peso_atual: data.perfil.peso_atual ?? prev.peso_atual,
          altura_cm: data.perfil.altura_cm ?? prev.altura_cm,
          meta_agua: data.perfil.meta_agua ?? prev.meta_agua,
          calorias_meta: data.perfil.calorias_meta ?? prev.calorias_meta,
          calorias_meta_manual:
            data.perfil.calorias_meta_manual !== undefined
              ? data.perfil.calorias_meta_manual
              : prev.calorias_meta_manual,
        }));
      }
    } catch (e) {
      console.error(e);
      setErro('Erro ao atualizar. Tente novamente em alguns instantes.');
    } finally {
      setSalvando(false);
    }
  };

  const ativarModoManual = async () => {
    await salvarCampo({
      calorias_meta_manual: true,
      calorias_meta: perfil.calorias_meta,
    });
  };

  const voltarParaAutomatico = async () => {
    await salvarCampo({ calorias_meta_manual: false });
  };

  const objetivoLabel = (valor) => {
    if (valor === 'emagrecer') return 'Emagrecer';
    if (valor === 'manter') return 'Manter o peso';
    if (valor === 'ganhar_peso') return 'Ganhar massa';
    return '—';
  };

  const nivelAtividadeLabel = (valor) => {
    if (valor === 'sedentario') return 'Sedentário';
    if (valor === 'leve') return 'Leve (1–2x/semana)';
    if (valor === 'moderado') return 'Moderado (3–4x/semana)';
    if (valor === 'intenso') return 'Intenso (5+x/semana)';
    return '—';
  };

  const formatarData = (iso) => {
    if (!iso) return '—';
    const [ano, mes, dia] = iso.split('-');
    return `${dia}/${mes}/${ano}`;
  };

  const dispararAvisoNotificacoes = () => {
    setAvisoNotificacoes(true);
    setTimeout(() => setAvisoNotificacoes(false), 3000);
  };

  return (
    <div className="perfil-page">
      <div className="perfil-container">
        <header className="perfil-header">
          <div className="perfil-header-info">
            <div className="perfil-avatar">
              {perfil.nome ? perfil.nome.charAt(0).toUpperCase() : 'U'}
            </div>
            <div>
              <h1>Configurações</h1>
              <p>Ajuste seus dados, metas e preferências.</p>
            </div>
          </div>

          <a href="/painel" className="perfil-voltar">
            ← Voltar para o painel
          </a>
        </header>

        <main className="perfil-main">
          {erro && <div className="perfil-alert perfil-alert-erro">{erro}</div>}
          {sucesso && (
            <div className="perfil-alert perfil-alert-sucesso">{sucesso}</div>
          )}

          {carregando ? (
            <div className="perfil-loading">Carregando dados do perfil...</div>
          ) : (
            <div className="perfil-layout">
              {/* MENU VERTICAL */}
              <nav className="perfil-abas">
                {ABAS.map((aba) => (
                  <button
                    key={aba.id}
                    className={`perfil-aba-item ${abaAtiva === aba.id ? 'ativa' : ''}`}
                    onClick={() => setAbaAtiva(aba.id)}
                  >
                    <FontAwesomeIcon icon={aba.icone} />
                    <span>{aba.label}</span>
                  </button>
                ))}
              </nav>

              {/* CONTEÚDO DA ABA */}
              <div className="perfil-conteudo">
                {abaAtiva === 'basicos' && (
                  <section className="perfil-secao">
                    <div className="perfil-campo-fixo">
                      <label>Nome</label>
                      <div className="perfil-valor-readonly">{perfil.nome || '—'}</div>
                    </div>

                    <div className="perfil-campo-fixo">
                      <label>E-mail</label>
                      <div className="perfil-valor-readonly">{perfil.email || '—'}</div>
                    </div>

                    <div className="perfil-campo-fixo">
                      <label>Gênero</label>
                      <div className="perfil-valor-readonly">{perfil.genero || '—'}</div>
                    </div>

                    <div className="perfil-campo-fixo">
                      <label>Data de nascimento</label>
                      <div className="perfil-valor-readonly">
                        {formatarData(perfil.data_nascimento)}
                      </div>
                    </div>
                  </section>
                )}

                {abaAtiva === 'metas' && (
                  <section className="perfil-secao">
                    <CampoEditavel
                      label="Peso atual (kg)"
                      valorExibido={perfil.peso_atual ? `${perfil.peso_atual} kg` : '—'}
                      valorBruto={perfil.peso_atual}
                      tipo="number"
                      salvando={salvando}
                      onSalvar={(v) => salvarCampo({ peso_atual: parseFloat(v) })}
                    />

                    <CampoEditavel
                      label="Altura (cm)"
                      valorExibido={perfil.altura_cm ? `${perfil.altura_cm} cm` : '—'}
                      valorBruto={perfil.altura_cm}
                      tipo="number"
                      salvando={salvando}
                      onSalvar={(v) => salvarCampo({ altura_cm: parseInt(v, 10) })}
                    />

                    <CampoEditavel
                      label="Meta diária de água (ml)"
                      valorExibido={perfil.meta_agua ? `${perfil.meta_agua} ml` : '—'}
                      valorBruto={perfil.meta_agua}
                      tipo="number"
                      salvando={salvando}
                      onSalvar={(v) => salvarCampo({ meta_agua: parseInt(v, 10) })}
                    />

                    <CampoEditavel
                      label="Objetivo"
                      valorExibido={objetivoLabel(perfil.objetivo)}
                      valorBruto={perfil.objetivo}
                      opcoes={[
                        { value: 'emagrecer', label: 'Emagrecer' },
                        { value: 'manter', label: 'Manter o peso' },
                        { value: 'ganhar_peso', label: 'Ganhar massa' },
                      ]}
                      salvando={salvando}
                      onSalvar={(v) => salvarCampo({ objetivo: v })}
                    />

                    <CampoEditavel
                      label="Nível de atividade"
                      valorExibido={nivelAtividadeLabel(perfil.nivel_atividade)}
                      valorBruto={perfil.nivel_atividade}
                      opcoes={[
                        { value: 'sedentario', label: 'Sedentário' },
                        { value: 'leve', label: 'Leve (1–2x/semana)' },
                        { value: 'moderado', label: 'Moderado (3–4x/semana)' },
                        { value: 'intenso', label: 'Intenso (5+x/semana)' },
                      ]}
                      salvando={salvando}
                      onSalvar={(v) => salvarCampo({ nivel_atividade: v })}
                    />

                    {/* META DE CALORIAS — automático vs manual */}
                    <div className="campo-editavel campo-calorias">
                      <label>Meta diária de calorias (kcal)</label>

                      {!perfil.calorias_meta_manual ? (
                        <>
                          <div className="campo-editavel-visualizacao">
                            <span className="campo-editavel-valor">
                              {perfil.calorias_meta || '—'} kcal
                            </span>
                          </div>
                          <small>
                            Calculado automaticamente com base em seus dados e objetivo.{' '}
                            <button
                              type="button"
                              className="perfil-link-modo"
                              onClick={ativarModoManual}
                              disabled={salvando}
                            >
                              Definir manualmente
                            </button>
                          </small>
                        </>
                      ) : (
                        <>
                          <CampoEditavel
                            label=""
                            valorExibido={`${perfil.calorias_meta} kcal`}
                            valorBruto={perfil.calorias_meta}
                            tipo="number"
                            salvando={salvando}
                            onSalvar={(v) =>
                              salvarCampo({
                                calorias_meta_manual: true,
                                calorias_meta: parseInt(v, 10),
                              })
                            }
                          />
                          <small>
                            Definido manualmente por você.{' '}
                            <button
                              type="button"
                              className="perfil-link-modo"
                              onClick={voltarParaAutomatico}
                              disabled={salvando}
                            >
                              Voltar para automático
                            </button>
                          </small>
                        </>
                      )}
                    </div>
                  </section>
                )}

                {abaAtiva === 'notificacoes' && (
                  <section className="perfil-secao">
                    {avisoNotificacoes && (
                      <div className="perfil-alert perfil-alert-info">
                        <FontAwesomeIcon icon={faBolt} /> Notificações chegando em breve!
                      </div>
                    )}

                    <div className="notificacao-item">
                      <div className="notificacao-item-info">
                        <FontAwesomeIcon icon={faDroplet} className="notificacao-icone" />
                        <span>Lembrete de beber água</span>
                      </div>
                      <button
                        type="button"
                        className="notificacao-switch"
                        onClick={dispararAvisoNotificacoes}
                      >
                        <span className="notificacao-switch-bola" />
                      </button>
                    </div>

                    <div className="notificacao-item">
                      <div className="notificacao-item-info">
                        <FontAwesomeIcon icon={faUtensils} className="notificacao-icone" />
                        <span>Lembrete de refeição</span>
                      </div>
                      <button
                        type="button"
                        className="notificacao-switch"
                        onClick={dispararAvisoNotificacoes}
                      >
                        <span className="notificacao-switch-bola" />
                      </button>
                    </div>
                  </section>
                )}
              </div>
            </div>
          )}
        </main>

        <Footer />
      </div>
    </div>
  );
};

export default Perfil;